/**
 * Order Math — centralized cost/payout engine for trade UX.
 *
 * Binary contract math:
 *   YES cost = priceCents / 100  per contract, payout = $1
 *   NO  cost = (100 - priceCents) / 100  per contract, payout = $1
 *
 * Kalshi taker fee: 7% of max profit per contract.
 * Polymarket: 0% fee currently.
 *
 * @ai-context
 *   Role: Canonical cost/payout engine. Every trade UX component uses this
 *         to compute what an order costs and what it returns.
 *   Pattern: Pure functions. All inputs in cents, all outputs in dollars.
 *   Purity: No side effects, no API calls.
 *
 * @ai-invariant priceCents input is always 0..100 cents. This module
 *   divides by 100 to get dollars. Passing fractional (0..1) produces
 *   100x undercount in cost and wildly wrong fee calculations.
 *
 * @ai-invariant Fee model delegates to perContractFeeCents (pricing.ts).
 *   Single source of truth — no local formula duplication (T-006).
 *
 * @ai-upstream
 *   - @/lib/nbaConsolidated/pricing → perContractFeeCents, KALSHI_TAKER_FEE_RATE, POLY_TAKER_FEE_RATE
 *
 * @ai-downstream
 *   - PlaceOrderModal → computeCost, contractsFromBudget, OrderCost
 *   - OpenOrdersPanel, CombinedOrdersPositionsPanel → computeCost
 *   - orderMath.test.ts (18 cases)
 */

import {
  KALSHI_TAKER_FEE_RATE,
  POLY_TAKER_FEE_RATE,
  perContractFeeCents,
} from './nbaConsolidated/pricing';

export type OrderSide = 'yes' | 'no';
export type Venue = 'kalshi' | 'polymarket';

export interface OrderCost {
  /** Total cost in dollars */
  costDollars: number;
  /** Maximum win (payout) in dollars if contract settles favorably */
  maxWinDollars: number;
  /** Fee in dollars (taker fee on profit) */
  feeDollars: number;
  /** Net profit = maxWin - cost - fee */
  maxProfitDollars: number;
}

export interface ContractPayout {
  /** Cost per single contract in dollars */
  cost: number;
  /** Payout per single contract if settled favorably ($1) */
  payout: number;
  /** Gross profit per contract (before fees) */
  profit: number;
}

/**
 * Compute cost per single contract for a given price and side.
 *
 * For YES: cost = priceCents / 100
 * For NO:  cost = (100 - priceCents) / 100
 */
export function computePayoutPerContract(priceCents: number, side: OrderSide): ContractPayout {
  // @ai-danger: priceCents must be 0..100. YES/NO complement: NO cost = 100 - YES cost.
  const costCents = side === 'yes' ? priceCents : 100 - priceCents;
  const cost = costCents / 100;
  const payout = 1; // binary contracts always settle at $1
  const profit = payout - cost;
  return { cost, payout, profit };
}

/**
 * Compute total order cost, max win, fee, and max profit.
 *
 * Fee model (Kalshi taker): fee = feeRate × contracts × P × (1-P)
 *   where P = priceCents / 100. This matches the existing `applyTakerFee` logic.
 * Polymarket: 0% fee.
 * Maker orders: 0% fee (isTaker = false).
 */
export function computeCost(
  priceCents: number,
  quantity: number,
  side: OrderSide,
  venue: Venue,
  isTaker: boolean
): OrderCost {
  const perContract = computePayoutPerContract(priceCents, side);
  const costDollars = perContract.cost * quantity;
  const maxWinDollars = perContract.payout * quantity;

  // Fee: only for taker orders
  let feeDollars = 0;
  if (isTaker) {
    const feeRate = venue === 'kalshi' ? KALSHI_TAKER_FEE_RATE : POLY_TAKER_FEE_RATE;
    feeDollars = (perContractFeeCents(priceCents, feeRate) / 100) * quantity;
  }

  const maxProfitDollars = maxWinDollars - costDollars - feeDollars;

  return { costDollars, maxWinDollars, feeDollars, maxProfitDollars };
}

/**
 * Compute number of contracts affordable for a given USD budget.
 */
export function contractsFromBudget(
  budgetDollars: number,
  priceCents: number,
  side: OrderSide
): number {
  const perContract = computePayoutPerContract(priceCents, side);
  if (perContract.cost <= 0) return 0;
  return Math.floor(budgetDollars / perContract.cost);
}

/**
 * Format an order cost summary for display.
 */
/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. UNIT CONTRACT — All inputs are priceCents (0..100). All dollar outputs
 *    come from dividing cents by 100. Never pass fractional (0..1).
 *
 * 2. FEE MODEL — Delegates to perContractFeeCents (pricing.ts).
 *    No local formula — T-006 resolved.
 *
 * 3. YES/NO COMPLEMENT — NO cost = 100 - priceCents. Both sides must sum
 *    to $1 per contract (binary market invariant).
 *
 * 4. DOWNSTREAM — PlaceOrderModal, OpenOrdersPanel, CombinedOrdersPositionsPanel
 *    all use computeCost. Changes affect trade UX directly.
 *
 * 5. TEST FILE — orderMath.test.ts (18 cases).
 *    Run: npm run dashboard:test -- orderMath
 */

/** Format an order cost summary for display. */
export function formatOrderSummary(cost: OrderCost): {
  cost: string;
  maxWin: string;
  maxProfit: string;
  fee: string;
} {
  const fmt = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return {
    cost: `$${fmt(cost.costDollars)}`,
    maxWin: `$${fmt(cost.maxWinDollars)}`,
    maxProfit: `$${fmt(cost.maxProfitDollars)}`,
    fee: cost.feeDollars > 0 ? `$${fmt(cost.feeDollars)}` : '$0.00',
  };
}
